/**
 * StepOutcomeGrid — the cross-rollout structure view: one row per
 * analyzed rollout, one column per golden-process step, cells colored
 * by that rollout's outcome on that step. The first column where
 * outcomes diverge is the task's branch point and gets a marker.
 */

import {
  branchPointIndex,
  humanizeTag,
  KNOWN_OUTCOMES,
  outcomeFill, processEntryFor } from "@/components/benchmarks/goldenProcess";
import { formatScore } from "@/components/evals/format";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type {
  ProcessRollout,
  RolloutEntry,
  TaskProcessResponse,
} from "@/api/generated/model";

export function StepOutcomeGrid({
  process,
  entries,
  selected,
  onSelect,
}: {
  process: TaskProcessResponse;
  entries: RolloutEntry[];
  selected: { model: string; rolloutId: string } | undefined;
  onSelect: (entry: RolloutEntry) => void;
}): React.JSX.Element {
  const branchIndex = branchPointIndex(process);
  const entryFor = (rollout: ProcessRollout): RolloutEntry | undefined =>
    processEntryFor(rollout, entries);

  return (
    <div className="space-y-3">
      {(() => {
        if (branchIndex === null) return null;
        const branchStep = process.steps[branchIndex];
        if (branchStep === undefined) return null;
        return (
          <p className="text-sm text-muted-foreground">
            Rollouts first diverge at step {branchIndex + 1} (
            <span className="text-foreground">{branchStep.title}</span>).
          </p>
        );
      })()}
      <p className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
        {KNOWN_OUTCOMES.map((outcome) => (
          <span key={outcome} className="inline-flex items-center gap-1.5">
            <span
              aria-hidden="true"
              className="size-2.5 rounded"
              style={{ backgroundColor: outcomeFill(outcome) }}
            />
            {humanizeTag(outcome)}
          </span>
        ))}
      </p>
      <div className="overflow-x-auto rounded-md border border-border p-3">
        <div className="min-w-fit">
          <div
            className="grid items-center gap-1.5"
            style={{
              gridTemplateColumns: `minmax(180px, max-content) repeat(${process.steps.length.toString()}, minmax(44px, 1fr)) 64px`,
            }}
          >
            <span />
            {process.steps.map((step, index) => (
              <span
                key={step.id}
                title={step.title}
                className={cn(
                  "text-center font-mono text-[10px] text-muted-foreground",
                  index === branchIndex && "text-brand-neutral-olive",
                )}
              >
                {index + 1}
              </span>
            ))}
            <span className="text-right font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              score
            </span>
            {process.rollouts.map((rollout) => {
              const entry = entryFor(rollout);
              const isSelected =
                selected !== undefined &&
                selected.model === rollout.model &&
                selected.rolloutId === rollout.rollout_id;
              const RowTag = entry !== undefined ? "button" : "span";
              return (
                <div
                  key={`${rollout.model}/${rollout.label}`}
                  className="contents"
                >
                  <RowTag
                    {...(entry !== undefined
                      ? {
                          type: "button" as const,
                          onClick: () => {
                            onSelect(entry);
                          },
                        }
                      : {})}
                    className={cn(
                      "truncate rounded px-1.5 py-0.5 text-left font-mono text-xs",
                      entry !== undefined
                        ? "cursor-pointer text-foreground hover:bg-muted/50"
                        : "text-muted-foreground",
                      isSelected && "bg-muted",
                    )}
                    title={`${rollout.model} · ${rollout.label}`}
                  >
                    {rollout.model} ·{" "}
                    {entry !== undefined ? rollout.rollout_id : rollout.label}
                  </RowTag>
                  {process.steps.map((step) => {
                    const run = rollout.step_runs.find(
                      (r) => r.step_id === step.id,
                    );
                    return (
                      <Tooltip key={step.id} delayDuration={500}>
                        <TooltipTrigger asChild>
                          <span
                            aria-label={
                              run !== undefined
                                ? `${step.title}: ${humanizeTag(run.outcome)}, turns ${run.turn_start.toString()} to ${run.turn_end.toString()}`
                                : `${step.title}: no data`
                            }
                            className="mx-auto block h-8 w-full max-w-14 rounded"
                            style={
                              run !== undefined
                                ? { backgroundColor: outcomeFill(run.outcome) }
                                : { border: "1px dashed hsl(var(--border))" }
                            }
                          />
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-80">
                          <p className="text-xs text-foreground">
                            {step.title}
                          </p>
                          <p className="font-mono text-xs text-muted-foreground">
                            {run !== undefined
                              ? `${humanizeTag(run.outcome)} · turns ${run.turn_start.toString()}-${run.turn_end.toString()}`
                              : "no data"}
                          </p>
                          {run !== undefined && run.what_happened !== "" && (
                            <p className="mt-1 max-w-72 text-xs text-muted-foreground">
                              {run.what_happened}
                            </p>
                          )}
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}
                  <span className="text-right font-mono text-xs">
                    {formatScore(rollout.score)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
